/* eslint-disable @typescript-eslint/no-explicit-any */
import dynamic from 'next/dynamic';
import React from 'react';
import { AccordionBlockProps } from '@/components/blocks/accordion-block/accordion-block';
import { FactBoxBlockProps } from '@/components/blocks/fact-box-block/fact-box-block';
import { ImageGalleryBlockProps } from '@/components/blocks/image-gallery-block/image-gallery-block';
import { ImageMediaBlockProps } from '@/components/blocks/image-media-block/image-media-block';
import { TextAndImageBlockProps } from '@/components/blocks/text-and-image-block/text-and-image-block';
import { TextBlockProps } from '@/components/blocks/text-block/text-block';
import { TwoColumnTextBlockProps } from '@/components/blocks/two-column-text-block/two-column-text-block';
import { VideoMediaBlockProps } from '@/components/blocks/video-media-block/video-media-block';

const AccordionBlock = dynamic(
  () => import('@/components/blocks/accordion-block/accordion-block'),
);
const FactBoxBlock = dynamic(
  () => import('@/components/blocks/fact-box-block/fact-box-block'),
);
const ImageGalleryBlock = dynamic(
  () => import('@/components/blocks/image-gallery-block/image-gallery-block'),
);
const ImageMediaBlock = dynamic(
  () => import('@/components/blocks/image-media-block/image-media-block'),
);
const TextBlock = dynamic(
  () => import('@/components/blocks/text-block/text-block'),
);
const TextAndImageBlock = dynamic(
  () => import('@/components/blocks/text-and-image-block/text-and-image-block'),
);
const TwoColumnTextBlock = dynamic(
  () =>
    import('@/components/blocks/two-column-text-block/two-column-text-block'),
);
const VideoMediaBlock = dynamic(
  () => import('@/components/blocks/video-media-block/video-media-block'),
);

export type ComposableContentModule =
  | ({
      $type: 'AccordionBlockDto';
    } & AccordionBlockProps)
  | ({
      $type: 'FactBoxBlockDto';
    } & FactBoxBlockProps)
  | ({
      $type: 'ImageGalleryBlockDto';
    } & ImageGalleryBlockProps)
  | ({
      $type: 'ImageMediaBlockDto';
    } & ImageMediaBlockProps)
  | ({
      $type: 'TextAndImageBlockDto';
    } & TextAndImageBlockProps)
  | ({
      $type: 'TextBlockDto';
    } & TextBlockProps)
  | ({
      $type: 'TwoColumnTextBlockDto';
    } & TwoColumnTextBlockProps)
  | ({
      $type: 'VideoMediaBlockDto';
    } & VideoMediaBlockProps);

const ComposableContentModuleMap: {
  [key in ComposableContentModule['$type']]: React.ComponentType<any>;
} = {
  AccordionBlockDto: AccordionBlock,
  FactBoxBlockDto: FactBoxBlock,
  ImageGalleryBlockDto: ImageGalleryBlock,
  ImageMediaBlockDto: ImageMediaBlock,
  TextAndImageBlockDto: TextAndImageBlock,
  TextBlockDto: TextBlock,
  TwoColumnTextBlockDto: TwoColumnTextBlock,
  VideoMediaBlockDto: VideoMediaBlock,
};

function getComposableContentModule(
  type: ComposableContentModule['$type'],
  props: Omit<ComposableContentModule, '$type'>,
): {
  component: React.ComponentType<Omit<ComposableContentModule, '$type'>>;
  props: Omit<ComposableContentModule, '$type'>;
} {
  return { component: ComposableContentModuleMap[type], props };
}

export interface ComposableContentModuleListProps {
  /** An array of the modules to show */
  contentModules: ComposableContentModule[];
}

const ComposableContentModuleList = ({
  contentModules = [],
}: ComposableContentModuleListProps) => {
  return (
    <React.Fragment>
      {contentModules.map((module, moduleIndex) => {
        const { component: Component, props } = getComposableContentModule(
          module.$type,
          module,
        );

        if (!Component) return null;

        return <Component key={moduleIndex} {...props} />;
      })}
    </React.Fragment>
  );
};

ComposableContentModuleList.displayName = 'ComposableContentModuleList';

export default ComposableContentModuleList;
